import React from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import {styled} from '@mui/system';

import WorkspacesPerUser from './WorkspacesPerUser';

import HeaderButton from '../component/atoms/HeaderButton';
import ProgressSpinner from '../component/atoms/ProgressSpinner';
import LinkButton from '../component/atoms/LinkButton';
import ActionButton from '../component/atoms/ActionButton';
import CustomAccordion from '../component/molecule/CustomAccordion';

import {concatUrl} from '../helpers/url';
import {checkUserRoles} from '../helpers/authentication';
import GlobalContext from '../helpers/context';
import {getCall, postCall} from '../helpers/request';


const StyledUserlist = styled(Paper)(({ theme }) =>({
    paddingLeft: theme.custom.paper.side_space,
    paddingRight: theme.custom.paper.side_space,
    paddingTop: theme.custom.paper.top_space,
    paddingBottom: theme.custom.paper.bottom_space,
    marginBottom: 30,
    minWidth: theme.custom.min_width
}));

const StyledUsersHeader = styled(Grid)(({ theme }) =>({
    padding: '10px 41px 10px 16px',
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    fontWeight: theme.custom.font.bold
}));

const StyledGrid = styled(Grid)(({ theme }) =>({
    fontSize: theme.custom.font.medium
}));

const StyledOptions = styled(Grid)({
    textAlign: 'right',
    paddingRight: 15
});

const StyledWorkspaces = styled('div')(({ theme }) =>({
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.fontSize
}));


class Users extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            users: null,
            user_id: null
        };

        this.is_mounted = false;
        this.accepted_roles = ['admin'];
        this.users_url = concatUrl(window.ROOT_URL, 'api', 'users');
        this.delete_user_url = concatUrl(window.ROOT_URL, 'api', 'user', 'delete');
    }

    componentDidMount(){
        this.is_mounted = true;
        this.context.checkUserAccess(this.conditionCallback);
        this.getUsers();
    }

    componentDidUpdate(){
        this.props.updatedCallback();
    }

    componentWillUnmount(){
        this.is_mounted = false;
    }

    conditionCallback = (user) => {
        return checkUserRoles(user, this.accepted_roles);
    };

    stopPropagation = (event) => {
        event.stopPropagation();
    };

    // +++ get users +++++++++++++++++++++++++++++++++++++++++++++++

    getUsersFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.context.displayGlobalError('Backend service failure: Could not receive user list.');
    };

    getUsersDone = (data, callback_failure) => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        if(data && data.users){
            this.setState({
                users: data.users
            });
        }else{
            callback_failure();
        }
    };

    getUsers = () => {
        getCall(this.users_url, this.getUsersDone, this.getUsersFailed);
    };


    // +++ delete an user +++++++++++++++++++++++++++++++++++++++++++++++

    deleteUserFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.context.displayGlobalError('Backend service failure: Could not delete the user.');
    };

    deleteUserDone = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.resetSelectedUser();
        this.getUsers();
    };

    deleteUser = () => {
        postCall(this.delete_user_url, {'user_id': this.state.user_id}, this.deleteUserDone, this.deleteUserFailed);
    };


    resetSelectedUser = () => {
        this.setState({
            user_id: null
        });
    };

    showConfirmationDialog = (event) => {
        this.stopPropagation(event);
        this.setState({
            user_id: event.currentTarget.dataset.user_id
        });

        this.context.showConfirmationDialog({
            confirm_dialog_on_close: this.resetSelectedUser,
            confirm_dialog_on_confirm: this.deleteUser,
            confirm_dialog_title: 'Please confirm',
            confirm_dialog_text: `Are you sure you want to delete the user ${event.currentTarget.dataset.full_name}?`,
            confirm_dialog_button_text: 'delete'
        });

    };

    showUsers(){
        if(this.state.users){
            return (
                <div>
                    {this.state.users.map((user) => <CustomAccordion
                        key={user.id}
                        dataBt={"user_item"}
                        summary={
                            <Box sx={{ flexGrow: 1 }}>
                                <Grid container spacing={2}>
                                    <StyledGrid size={3} data-bt="user_email">
                                        {user.email}
                                    </StyledGrid>
                                    <StyledGrid size={3} data-bt="full_name">
                                        {user.full_name}
                                    </StyledGrid>
                                    <StyledGrid size={3} data-bt="user_organization">
                                        {user.organization}
                                    </StyledGrid>
                                    <StyledGrid size={2} data-bt="user_group">
                                        {user.groups}
                                    </StyledGrid>
                                    <StyledOptions size={1} data-bt="user_group">
                                        <LinkButton text="Edit" linkStyle="light" dataBt="edit_user_button"
                                                    linkData={{pathname: '/users/edit', search: `?id=${user.id}`}} />
                                        <ActionButton text="Delete" linkStyle={true}
                                              actionCallback={this.showConfirmationDialog}
                                              actionData={{user_id: user.id, full_name: user.full_name}}
                                              dataBt="delete_user_button" />
                                    </StyledOptions>
                                </Grid>
                            </Box>
                        }
                        content={
                            <StyledWorkspaces>
                                {<WorkspacesPerUser user_id={user.id} />}
                            </StyledWorkspaces>
                        }
                    />)}
                </div>
            );
        }
    }

    render() {
        return (
            <GlobalContext.Consumer>{() => (
                <StyledUserlist data-bt="users">
                    <Grid container justifyContent="space-between">
                        <Grid>
                            <Typography variant="h5">
                                Users
                            </Typography>
                        </Grid>
                        <Grid>
                            <HeaderButton text="Create user" dataBt="create_user" linkData={{pathname: '/users/create'}} hotKey="alt+n"/>
                        </Grid>
                    </Grid>
                    <StyledUsersHeader container spacing={2}>
                        <Grid size={3}>
                            Email
                        </Grid>
                        <Grid size={3}>
                            Name
                        </Grid>
                        <Grid size={3}>
                            Organizations
                        </Grid>
                        <Grid size={2}>
                            Group
                        </Grid>
                        <StyledOptions size={1}/>
                    </StyledUsersHeader>
                    {!this.state.users && <ProgressSpinner />}
                    {this.showUsers()}
                </StyledUserlist>
            )}
            </GlobalContext.Consumer>
        );
    }
}

Users.contextType = GlobalContext;

Users.propTypes = {
    updatedCallback: PropTypes.func
};

export default Users;
